import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
  Shield,
  Gauge,
  Settings,
  Wrench,
  Building2,
  Users,
  FileCheck,
  Clock,
  Award,
  ExternalLink
} from 'lucide-react';

const EtalonnageInstrumentation = () => {
  // Schema.org LocalBusiness
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "SIM Métrologie",
    "description": "Étalonnage, location et maintenance d'instrumentation géotechnique pour la géotechnique et les fondations spéciales. Capteurs de forage, enregistreurs de paramètres.",
    "image": "https://geots.consulting/og-images/sim-metrologie.jpg",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "",
      "addressLocality": "Combs-la-Ville",
      "postalCode": "77380",
      "addressRegion": "Île-de-France",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 48.6697,
      "longitude": 2.5503
    },
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "serviceType": [
      "Étalonnage de capteurs de forage",
      "Location d'instrumentation géotechnique",
      "Maintenance métrologique",
      "Enregistreurs de paramètres"
    ],
    "priceRange": "€€"
  };

  const services = [
    {
      icon: Gauge,
      title: "Étalonnage de capteurs de forage et d'injection",
      description: "Étalonnage et vérification de vos capteurs (pression, couple, profondeur, inclinaison, débit), avec certificat et traçabilité métrologique au Système International d'unités."
    },
    {
      icon: Settings,
      title: "Location d'instrumentation géotechnique",
      description: "Mise à disposition de matériel déjà étalonné et vérifié : enregistreurs de paramètres, capteurs, systèmes d'acquisition. Intervenez avec un matériel fiable."
    },
    {
      icon: Wrench,
      title: "Maintenance et suivi de parc",
      description: "Entretien, réparation et gestion des échéances d'étalonnage de votre parc d'instrumentation. SIM veille à ce que vos capteurs restent conformes et disponibles."
    },
    {
      icon: Award,
      title: "Distributeur agréé Jean Lutz",
      description: "SIM travaille notamment avec l'instrumentation Jean Lutz, référence de l'enregistrement de paramètres sur les chantiers de fondations spéciales."
    }
  ];

  const targetAudiences = [
    {
      icon: Building2,
      title: "Entreprises de forage et fondations spéciales",
      description: "Pieux, micropieux, parois, injection"
    },
    {
      icon: FileCheck,
      title: "Bureaux d'études géotechniques",
      description: "Essais in situ, instrumentation de suivi"
    },
    {
      icon: Users,
      title: "Loueurs et exploitants de matériel",
      description: "Suivi métrologique de parc"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Étalonnage et instrumentation géotechnique — SIM Métrologie | Intervention France</title>
        <meta name="description" content="Étalonnage, location et maintenance d'instrumentation géotechnique pour la géotechnique et les fondations spéciales. Capteurs de forage, enregistreurs de paramètres. Devis rapide." />
        <link rel="canonical" href="https://geots.consulting/annuaire/etalonnage-instrumentation-geotechnique" />
        <meta property="og:title" content="Étalonnage et instrumentation géotechnique — SIM Métrologie" />
        <meta property="og:description" content="Étalonnage, location et maintenance d'instrumentation géotechnique. Capteurs de forage, enregistreurs de paramètres. Intervention France." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/annuaire/etalonnage-instrumentation-geotechnique" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/annuaire" className="hover:text-[#635BFF]">Annuaire</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Étalonnage instrumentation</span>
            </nav>
          </div>
        </div>

        {/* Hero Section with CTA */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  PARTENAIRE GEOTS
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Combs-la-Ville (77) · France
                </span>
              </div>
              
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                Étalonnage et instrumentation géotechnique
                <span className="text-[#00D4FF]"> — pour la géotechnique et les fondations spéciales</span>
              </h1>
              
              <p className="text-lg text-gray-300 mb-8">
                Besoin d'étalonner vos capteurs ou de louer de l'instrumentation déjà étalonnée ?
              </p>

              {/* Primary CTA - visible sans scroller */}
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/materiel/demande?type=instrumentation&objet=etalonnage"
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Demander un devis
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/materiel/demande?type=instrumentation&objet=rappel"
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors border border-white/20"
                >
                  Être rappelé
                  <Phone className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                
                {/* Quick Contact Card */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#635BFF]" />
                    SIM Métrologie
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 mt-0.5 text-[#635BFF]" />
                      <span>Combs-la-Ville (77380)<br />Île-de-France</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Shield className="w-4 h-4 text-[#635BFF]" />
                      <span>Intervention France entière</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <Link 
                      to="/materiel/demande?type=instrumentation&objet=contact"
                      className="w-full bg-[#635BFF] hover:bg-[#5248e5] text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      Nous contacter
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Services rapides */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4">Prestations</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Étalonnage capteurs
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Location instrumentation
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Maintenance de parc
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Distributeur Jean Lutz
                    </li>
                  </ul>
                </div>

                {/* Link to guide */}
                <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-4">
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>Pour approfondir :</strong>
                  </p>
                  <Link 
                    to="/guides/etalonnage-capteurs-forage"
                    className="text-[#635BFF] hover:underline text-sm font-medium flex items-center gap-1"
                  >
                    Guide : Étalonnage des capteurs de forage
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Introduction */}
                <section className="mb-12">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    <strong>SIM Métrologie</strong> est spécialisée dans <strong>l'étalonnage, la location et la maintenance 
                    de l'instrumentation géotechnique</strong> pour les chantiers de géotechnique et de fondations spéciales. 
                    Enregistreurs de paramètres de forage et d'injection, capteurs de pression, de couple, de profondeur 
                    et d'inclinaison : SIM assure la fiabilité et la traçabilité de vos mesures.
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-[#635BFF]" />
                      <strong>Intervention sur toute la France</strong>
                    </span>
                    <span>·</span>
                    <span>Basée en Île-de-France (Combs-la-Ville, 77)</span>
                  </div>
                </section>

                {/* Prestations */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">
                    Nos prestations d'étalonnage et d'instrumentation
                  </h2>
                  <div className="space-y-6">
                    {services.map((service, index) => (
                      <div key={index} className="bg-white rounded-xl border p-6">
                        <div className="flex items-start gap-4">
                          <div className="bg-[#635BFF]/10 p-3 rounded-xl">
                            <service.icon className="w-6 h-6 text-[#635BFF]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-2">{service.title}</h3>
                            <p className="text-gray-700">{service.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Pourquoi un spécialiste */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">
                    Pourquoi confier votre instrumentation à un spécialiste
                  </h2>
                  <p className="text-gray-700 mb-6">
                    Sur un chantier de fondations spéciales, vos mesures sont la <strong>preuve de la bonne exécution</strong> de 
                    l'ouvrage. Un capteur dérivé, et c'est toute la traçabilité qui devient contestable — en audit comme en cas de litige.
                  </p>
                  <p className="text-gray-700 mb-6">
                    Faire appel à un prestataire spécialisé dans l'instrumentation <strong>géotechnique</strong> (et non un 
                    laboratoire généraliste) garantit :
                  </p>
                  <div className="bg-white rounded-xl border p-6">
                    <ul className="space-y-3">
                      {[
                        "Des capteurs étalonnés dans les conditions réelles de leur usage",
                        "Une traçabilité métrologique conforme aux exigences (norme NF EN ISO/IEC 17025:2017)",
                        "Une réactivité adaptée aux contraintes de chantier — un capteur immobilisé, c'est un chantier retardé",
                        "Une connaissance fine de votre matériel (enregistreurs de paramètres, capteurs de forage et d'injection)"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-6 bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-lg p-4">
                    <p className="text-sm text-[#0A2540]">
                      <strong>Pour approfondir :</strong> consultez notre{' '}
                      <Link to="/guides/etalonnage-capteurs-forage" className="text-[#635BFF] hover:underline">
                        guide sur l'étalonnage des capteurs de forage
                      </Link>.
                    </p>
                  </div>
                </section>

                {/* Pour qui */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Pour qui ?</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    {targetAudiences.map((audience, index) => (
                      <div key={index} className="bg-white rounded-xl border p-4 text-center">
                        <div className="bg-[#635BFF]/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <audience.icon className="w-6 h-6 text-[#635BFF]" />
                        </div>
                        <h3 className="font-semibold text-[#0A2540] text-sm mb-1">{audience.title}</h3>
                        <p className="text-xs text-gray-500">{audience.description}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Zone d'intervention */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Zone d'intervention</h2>
                  <div className="bg-white rounded-xl border p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-green-100 p-3 rounded-xl">
                        <MapPin className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-gray-700">
                          SIM Métrologie intervient sur l'ensemble du <strong>territoire français</strong>, avec un ancrage 
                          en <strong>Île-de-France (Combs-la-Ville, 77)</strong>. Étalonnage en atelier ou intervention selon vos besoins.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* CTA Final Block */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white">
                  <h2 className="text-2xl font-bold mb-4">Demander un devis ou nous contacter</h2>
                  <p className="text-white/90 mb-6">
                    Vous cherchez à étalonner vos capteurs, à louer de l'instrumentation déjà étalonnée, 
                    ou à confier le suivi métrologique de votre parc ?
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Link 
                      to="/materiel/demande?type=instrumentation&objet=etalonnage"
                      className="bg-white text-[#635BFF] px-4 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <FileCheck className="w-5 h-5" />
                      Devis étalonnage
                    </Link>
                    <Link 
                      to="/materiel/demande?type=instrumentation&objet=location"
                      className="bg-white/20 text-white px-4 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center justify-center gap-2 text-sm border border-white/30"
                    >
                      <Settings className="w-5 h-5" />
                      Louer instrumentation
                    </Link>
                    <Link 
                      to="/materiel/demande?type=instrumentation&objet=contact"
                      className="bg-white/20 text-white px-4 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center justify-center gap-2 text-sm border border-white/30"
                    >
                      <Phone className="w-5 h-5" />
                      Nous contacter
                    </Link>
                  </div>
                  <p className="text-white/70 text-sm mt-6 text-center">
                    GEOTS vous met en relation avec SIM Métrologie et les spécialistes de l'instrumentation géotechnique.
                  </p>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8 mt-8">
                  <p>
                    SIM Métrologie — étalonnage, location et maintenance d'instrumentation géotechnique. 
                    Intervention nationale, basée en Île-de-France (77).
                  </p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default EtalonnageInstrumentation;
